import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import { InvitationCollectionItem } from '@app/v2/event/types/invitation-tracker.type'
import {
  canRemoveInvitationCollectionItem,
  formatInvitationCollectionActionTakenLabel,
  invitationCollectionActionTakenBadgeClass
} from '@app/v2/event/utils/invitation-action-taken.util'
import {
  getInvitationCollectionChannelStatuses,
  InvitationChannel,
  INVITATION_CHANNEL_COMING_SOON_TOOLTIP,
  invitationChannelDotClass,
  invitationChannelIconFill,
  isInvitationChannelAvailable
} from '@app/v2/event/utils/invitation-channel-status.util'
import { formatInvitationCollectionSourceLabel } from '@app/v2/event/utils/invitation-source.util'
import { TruncationDetectorDirective } from '@app/v2/shared/directives/truncation-detector.directive'
import { NgxTippyModule } from 'ngx-tippy-wrapper'

export type InvitationChannelOpenRequest = {
  collectionItemId: number
  channel: InvitationChannel
}

@Component({
  selector: 'app-invitaiton-tracker-mobile-card',
  standalone: true,
  imports: [NgClass, NgxTippyModule, TruncationDetectorDirective],
  templateUrl: './invitaiton-tracker-mobile-card.component.html',
  styleUrl: './invitaiton-tracker-mobile-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvitaitonTrackerMobileCardComponent {
  public readonly item = input.required<InvitationCollectionItem>()
  public readonly isSelected = input.required<boolean>()
  public readonly selectionToggle = output<void>()
  public readonly removeItem = output<InvitationCollectionItem>()
  public readonly openInvitationChannel = output<InvitationChannelOpenRequest>()
  public readonly sourceClick = output<InvitationCollectionItem>()

  public readonly invitationCountLabel = computed(() => {
    const count = this.item().count
    return `${count} invitation${count === 1 ? '' : 's'}`
  })

  public readonly invitationsCostSummary = computed(() => {
    const cost = String(this.item().cost ?? '')
      .trim()
      .replace(/^\$/, '')
    return `${this.invitationCountLabel()} · $${cost || '0'}`
  })

  public readonly canRemove = computed(() => canRemoveInvitationCollectionItem(this.item()))

  public readonly actionTakenLabel = computed(() => formatInvitationCollectionActionTakenLabel(this.item().action_taken))

  public readonly actionTakenBadgeClass = computed(() => invitationCollectionActionTakenBadgeClass(this.item().action_taken))

  public readonly sourceLabel = computed(() => formatInvitationCollectionSourceLabel(this.item()))

  public readonly channelStatuses = computed(() => getInvitationCollectionChannelStatuses(this.item()))

  public readonly channelDotClass = invitationChannelDotClass
  public readonly comingSoonTooltip = INVITATION_CHANNEL_COMING_SOON_TOOLTIP

  /**
   * Whether the invitee has contact details required for the channel.
   * @param channel {InvitationChannel} - Channel to check.
   */
  public isChannelAvailable(channel: InvitationChannel): boolean {
    return isInvitationChannelAvailable(this.item(), channel)
  }

  /**
   * SVG fill for available vs unavailable channel icons (gray-700 / gray-400).
   * @param available {boolean} - Whether the channel can be used.
   */
  public channelIconFill(available: boolean): string {
    return invitationChannelIconFill(available)
  }

  /**
   * Toggle the selected state of the invitee card.
   */
  public toggleSelected(): void {
    this.selectionToggle.emit()
  }

  /**
   * Forward remove action to the parent.
   */
  public onRemove(): void {
    this.removeItem.emit(this.item())
  }

  /**
   * Forward the source click to the parent to open the data source drawer.
   */
  public onSourceClick(): void {
    this.sourceClick.emit(this.item())
  }

  /**
   * Opens the invitation channel mobile modal for email or WhatsApp when that channel is available.
   * SMS stays non-interactive for now.
   * @param channel {InvitationChannel} - Channel selected from the card.
   */
  public onOpenInvitationChannel(channel: InvitationChannel): void {
    if ((channel !== 'email' && channel !== 'whatsapp') || !this.isChannelAvailable(channel)) return
    this.openInvitationChannel.emit({
      collectionItemId: this.item().id,
      channel
    })
  }
}
